import { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FiMenu, FiX, FiHome, FiUser, FiLogOut,
  FiGlobe, FiMonitor, FiMoon, FiSun, FiShield
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTheme } from '../contexts/ThemeContext'

const Navbar = ({ scrolled }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuth()
  const { currentLanguage, toggleLanguage } = useLanguage()
  const { isDarkMode, toggleTheme } = useTheme()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isHome = location.pathname === '/'
  const showSolidNav = scrolled || mobileMenuOpen || !isHome

  const navItems = [
    { path: '/', label: t('nav.home'), icon: FiHome },
    { path: '#games', label: t('nav.games', 'Games'), icon: FiMonitor, isAnchor: true },
    { path: '#about', label: t('nav.about'), icon: FiUser, isAnchor: true },
  ]

  const handleLogout = async () => {
    await logout()
    setMobileMenuOpen(false)
  }

  const NavItem = ({ item, mobile = false }) => {
    const className = ({ isActive }) =>
      `flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${
        isActive && !item.isAnchor
          ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
          : showSolidNav
          ? 'text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          : 'text-white/90 hover:text-white hover:bg-white/10'
      } ${mobile ? 'w-full text-lg' : ''}`

    if (item.isAnchor) {
      return (
        <a
          href={item.path}
          onClick={() => mobile && setMobileMenuOpen(false)}
          className={showSolidNav 
            ? 'flex items-center space-x-2 px-4 py-2 rounded-xl text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-300' 
            : 'flex items-center space-x-2 px-4 py-2 rounded-xl text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300'}
        >
          <item.icon className={mobile ? "w-5 h-5" : "w-4 h-4"} />
          <span>{item.label}</span>
        </a>
      )
    }

    return (
      <NavLink
        to={item.path}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={className}
      >
        <item.icon className={mobile ? "w-5 h-5" : "w-4 h-4"} />
        <span>{item.label}</span>
      </NavLink>
    )
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showSolidNav
          ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg shadow-premium border-b border-slate-200/50 dark:border-slate-800/50 py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 group"
          >
            <div className="relative">
              <img
                src="/JoyeeLOGO.jpg"
                alt="JoyeeAssets"
                className="h-10 w-10 rounded-xl shadow-lg transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 rounded-xl bg-primary-500/20 group-hover:bg-transparent transition-colors" />
            </div>
            <span className={`text-xl font-bold tracking-tight transition-colors duration-300 ${
              showSolidNav ? 'text-slate-900 dark:text-white' : 'text-white'
            }`}>
              久夷 <span className="text-primary-500">Joyee</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => (
              <NavItem key={item.label} item={item} />
            ))}

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2" />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-xl transition-all duration-300 ${
                showSolidNav
                  ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  : 'text-white/90 hover:bg-white/10'
              }`}
            >
              {isDarkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-all duration-300 ${
                showSolidNav
                  ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  : 'text-white/90 hover:bg-white/10'
              }`}
            >
              <FiGlobe className="w-4 h-4" />
              <span className="font-medium">{currentLanguage.toUpperCase()}</span>
            </button>

            {/* User Profile/Login */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-2 ml-4">
                {user?.is_superuser && (
                  <NavLink
                    to="/admin/users"
                    className={({ isActive }) =>
                      `flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                        isActive
                          ? 'bg-amber-600 text-white shadow-lg shadow-amber-500/30'
                          : showSolidNav
                          ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                          : 'text-amber-300 hover:bg-amber-500/20'
                      }`
                    }
                  >
                    <FiShield className="w-4 h-4" />
                    <span>Admin</span>
                  </NavLink>
                )}
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    `flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                      isActive
                        ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                        : showSolidNav
                        ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        : 'text-white/90 hover:bg-white/10'
                    }`
                  }
                >
                  <FiUser className="w-4 h-4" />
                  <span>{t('nav.profile')}</span>
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="p-2 bg-rose-500/10 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all duration-300"
                  title={t('nav.logout')}
                >
                  <FiLogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <NavLink
                to="/login"
                className="ml-4 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/25 transform hover:scale-105"
              >
                {t('nav.login')}
              </NavLink>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-xl transition-all duration-300 ${
              showSolidNav
                ? 'text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                : 'text-white hover:bg-white/10'
            }`}
          >
            {mobileMenuOpen ? (
              <FiX className="w-6 h-6" />
            ) : (
              <FiMenu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-4 bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
            >
              <div className="p-6 space-y-3">
                {navItems.map((item) => (
                  <NavItem key={item.label} item={item} mobile />
                ))}
                
                <div className="h-px bg-slate-100 dark:bg-slate-800 my-4" />
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={toggleTheme}
                    className="flex items-center justify-center space-x-2 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium active:scale-95 transition-all"
                  >
                    {isDarkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
                    <span>{isDarkMode ? 'Light' : 'Dark'}</span>
                  </button>
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center justify-center space-x-2 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium active:scale-95 transition-all"
                  >
                    <FiGlobe className="w-5 h-5" />
                    <span>{currentLanguage.toUpperCase()}</span>
                  </button>
                </div>

                <div className="pt-2">
                  {isAuthenticated ? (
                    <div className="space-y-3">
                      {user?.is_superuser && (
                        <NavLink
                          to="/admin/users"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center space-x-3 p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-medium active:scale-95 transition-all"
                        >
                          <FiShield className="w-5 h-5" />
                          <span>Admin Panel</span>
                        </NavLink>
                      )}
                      <NavLink
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium active:scale-95 transition-all"
                      >
                        <FiUser className="w-5 h-5" />
                        <span>{t('nav.profile')}</span>
                      </NavLink>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center space-x-2 p-4 rounded-2xl bg-rose-500 text-white font-bold shadow-lg shadow-rose-500/20 active:scale-95 transition-all"
                      >
                        <FiLogOut className="w-5 h-5" />
                        <span>{t('nav.logout')}</span>
                      </button>
                    </div>
                  ) : (
                    <NavLink
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center p-4 rounded-2xl bg-primary-600 text-white font-bold shadow-lg shadow-primary-500/25 active:scale-95 transition-all"
                    >
                      {t('nav.login')}
                    </NavLink>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  )
}

export default Navbar
